import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  SxProps,
  Tab,
  Tabs,
  Theme,
  Typography,
} from '@mui/material';

interface TabbedEditorDialogTab {
  disabled?: boolean;
  label: React.ReactNode;
}

interface TabbedEditorDialogProps {
  activeTab: number;
  actions?: React.ReactNode;
  children: React.ReactNode;
  contentSx?: SxProps<Theme>;
  disableClose?: boolean;
  maxWidth?: DialogProps['maxWidth'];
  onClose: () => void;
  onTabChange: (value: number) => void;
  open: boolean;
  paperSx?: SxProps<Theme>;
  tabs: TabbedEditorDialogTab[];
  title: React.ReactNode;
}

// NOTE: layout-only wrapper; keep feature-specific state and submit logic in the caller.
const TabbedEditorDialog: React.FC<TabbedEditorDialogProps> = ({
  activeTab,
  actions,
  children,
  contentSx,
  disableClose = false,
  maxWidth = 'md',
  onClose,
  onTabChange,
  open,
  paperSx,
  tabs,
  title,
}) => {
  return (
    <Dialog
      open={open}
      onClose={disableClose ? undefined : onClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: paperSx,
      }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
        <IconButton onClick={onClose} disabled={disableClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={activeTab} onChange={(_, value: number) => onTabChange(value)}>
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} disabled={tab.disabled} />
          ))}
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 1, ...contentSx }}>{children}</DialogContent>

      {actions ? <DialogActions sx={{ px: 3, pb: 2 }}>{actions}</DialogActions> : null}
    </Dialog>
  );
};

export default TabbedEditorDialog;
